const UUID_V4_PATTERN =
  /^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i;

type RandomBytes = Uint8Array<ArrayBuffer>;

function formatUuidV4(bytes: RandomBytes): string {
  const normalized = bytes.slice();
  normalized[6] = (normalized[6] & 0x0f) | 0x40;
  normalized[8] = (normalized[8] & 0x3f) | 0x80;

  const hex = Array.from(normalized, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function generateUuidV4FromRandomBytes(
  getRandomBytes: (bytes: RandomBytes) => RandomBytes
): string {
  const bytes = new Uint8Array(new ArrayBuffer(16));
  getRandomBytes(bytes);
  return formatUuidV4(bytes);
}

/**
 * Correlation ID for outgoing API requests. Prefers Web Crypto randomUUID where
 * available (browsers); falls back to getRandomValues (React Native / Hermes)
 * and finally Math.random so wallet API calls do not throw before fetch runs.
 */
export function generateRequestId(): string {
  const webCrypto = globalThis.crypto;

  if (typeof webCrypto?.randomUUID === "function") {
    return webCrypto.randomUUID();
  }

  if (typeof webCrypto?.getRandomValues === "function") {
    return generateUuidV4FromRandomBytes((bytes) =>
      webCrypto.getRandomValues(bytes)
    );
  }

  return generateUuidV4FromRandomBytes((bytes) => {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
    return bytes;
  });
}

export function isUuidV4(value: string): boolean {
  return UUID_V4_PATTERN.test(value);
}
